import { forwardRef } from 'react';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { BenchObjectTableRow, CellGrow } from '../../BenchObjectTable/ui';
import { FirstCellSkeleton } from './FirstCellSkeleton';
import { LastCellSkeleton } from './LastCellSkeleton';
import { MatchupsSkeleton } from './MatchupsSkeleton';
import { ScoreSkeleton } from './ScoreSkeleton';
import { UpcomingGamesSkeleton } from './UpcomingGamesSkeleton';

type Props = {
  variant?: 'COMPOSE' | 'SCOUTING';
};

const StatsSkeleton = styled(Skeleton)`
  height: var(--double-and-a-half-unit);
  width: var(--quadruple-unit);
`;

export const BenchObjectSkeleton = forwardRef<HTMLDivElement, Props>(
  ({ variant = 'SCOUTING' }, ref) => {
    const { statsManager } = useManagePlayerSearchView();
    return (
      <BenchObjectTableRow ref={ref}>
        <FirstCellSkeleton variant={variant} />
        {variant === 'COMPOSE' && <MatchupsSkeleton />}
        {variant === 'SCOUTING' && <UpcomingGamesSkeleton />}
        {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
          e => {
            return <ScoreSkeleton variant="average-score" key={e} />;
          }
        )}
        {statsManager[PlayerSearchViewAttributes.PROJECTION].map(e => {
          return (
            <Vertical key={e} center>
              <StatsSkeleton />
            </Vertical>
          );
        })}
        {[
          ...statsManager[PlayerSearchViewAttributes.BREAKDOWN],
          ...statsManager[PlayerSearchViewAttributes.STATS],
        ].map(e => {
          return (
            <Vertical key={e} center>
              <StatsSkeleton />
            </Vertical>
          );
        })}
        {variant === 'SCOUTING' && (
          <>
            <CellGrow />
            <LastCellSkeleton />
          </>
        )}
      </BenchObjectTableRow>
    );
  }
);

BenchObjectSkeleton.displayName = 'BenchObjectSkeleton';
